import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  User,
  ArrowLeft,
  ExternalLink,
  Loader2,
  X,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';

export default function OfficerComplaints() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [showResolvedModal, setShowResolvedModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [clientSatisfied, setClientSatisfied] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const response = await api.get('/officer/complaints');
      setComplaints(response.data);
    } catch (error) {
      console.error('Failed to fetch complaints:', error);
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'in_progress':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'resolved':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'closed':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <AlertTriangle size={16} />;
      case 'in_progress':
        return <Clock size={16} />;
      case 'resolved':
        return <CheckCircle size={16} />;
      default:
        return <MessageSquare size={16} />;
    }
  };

  const filteredComplaints = selectedStatus === 'all'
    ? complaints
    : complaints.filter(c => c.status === selectedStatus);

  const statusCounts = {
    all: complaints.length,
    open: complaints.filter(c => c.status === 'open').length,
    in_progress: complaints.filter(c => c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
    closed: complaints.filter(c => c.status === 'closed').length,
  };

  const handleMarkInProgress = (complaint) => {
    setSelectedComplaint(complaint);
    setResolutionNotes('');
    setEstimatedTime('');
    setShowInProgressModal(true);
  };

  const handleMarkResolved = (complaint) => {
    setSelectedComplaint(complaint);
    setResolutionNotes('');
    setClientSatisfied(null);
    setShowResolvedModal(true);
  };

  const submitInProgress = async () => {
    if (!resolutionNotes || !estimatedTime) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/complaints/${selectedComplaint.id}`, {
        status: 'in_progress',
        resolution_notes: resolutionNotes,
        estimated_resolution_time: estimatedTime
      });
      toast.success('Complaint marked as In Progress');
      setShowInProgressModal(false);
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const submitResolved = async () => {
    if (!resolutionNotes || clientSatisfied === null) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/complaints/${selectedComplaint.id}`, {
        status: 'resolved',
        resolution_notes: resolutionNotes,
        client_satisfied: clientSatisfied
      });
      toast.success('Complaint marked as Resolved');
      setShowResolvedModal(false);
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update complaint');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="officer-complaints-page">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/officer')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-all"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
            Client Complaints
          </h1>
          <p className="text-slate-600">Complaints from clients on your assigned cases</p>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="status-filters">
          {[
            { key: 'all', label: 'All' },
            { key: 'open', label: 'Open' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'resolved', label: 'Resolved' },
            { key: 'closed', label: 'Closed' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setSelectedStatus(tab.key)}
              data-testid={`filter-${tab.key}`}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedStatus === tab.key
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label} ({statusCounts[tab.key]})
            </button>
          ))}
        </div>

        {/* Complaints List */}
        {filteredComplaints.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <MessageSquare size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Complaints Found</h3>
            <p className="text-slate-600">
              {selectedStatus === 'all'
                ? 'No complaints have been raised on your assigned cases yet.'
                : `No ${selectedStatus.replace('_', ' ')} complaints.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="complaints-list">
            {filteredComplaints.map(complaint => (
              <div
                key={complaint.id}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-all"
                data-testid={`complaint-${complaint.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getStatusColor(complaint.status)}`}>
                      {getStatusIcon(complaint.status)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{complaint.title || complaint.subject}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {complaint.client_name}
                        </span>
                        <span>•</span>
                        <span>Case #{complaint.case_id?.slice(0, 8)}</span>
                        <span>•</span>
                        <span>{new Date(complaint.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(complaint.status)}`}>
                    {complaint.status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <p className="text-slate-700 mb-4 pl-12">{complaint.description}</p>
                
                {/* Resolution Notes (if in progress or resolved) */}
                {complaint.resolution_notes && (
                  <div className={`rounded-lg p-3 ml-12 mb-3 ${
                    complaint.status === 'in_progress' ? 'bg-amber-50 border border-amber-200' :
                    complaint.status === 'resolved' ? 'bg-green-50 border border-green-200' : 'bg-slate-50'
                  }`}>
                    <p className="text-xs font-medium text-slate-600 mb-1">
                      {complaint.status === 'in_progress' ? 'Resolution Under Process:' : 'Resolution:'}
                    </p>
                    <p className="text-sm text-slate-700">{complaint.resolution_notes}</p>
                    {complaint.estimated_resolution_time && complaint.status === 'in_progress' && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <Clock size={12} />
                        Estimated Time: {complaint.estimated_resolution_time}
                      </p>
                    )}
                    {complaint.client_satisfied !== null && complaint.status === 'resolved' && (
                      <p className={`text-xs mt-2 flex items-center gap-1 ${
                        complaint.client_satisfied ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {complaint.client_satisfied ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                        Client {complaint.client_satisfied ? 'Satisfied' : 'Not Satisfied'}
                      </p>
                    )}
                  </div>
                )}
                
                {complaint.admin_response && (
                  <div className="bg-slate-50 rounded-lg p-3 ml-12 mb-3">
                    <p className="text-xs text-slate-500 mb-1">Admin Response:</p>
                    <p className="text-sm text-slate-700">{complaint.admin_response}</p>
                  </div>
                )}
                
                <div className="flex items-center justify-between pl-12">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="px-2 py-1 bg-slate-100 rounded text-xs">{complaint.complaint_type}</span>
                    {complaint.updated_at && complaint.updated_at !== complaint.created_at && (
                      <span>Updated: {new Date(complaint.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Action buttons for open complaints */}
                    {complaint.status === 'open' && (
                      <>
                        <button
                          onClick={() => handleMarkInProgress(complaint)}
                          className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-all flex items-center gap-1"
                          data-testid={`in-progress-btn-${complaint.id}`}
                        >
                          <Clock size={14} />
                          Resolution Under Process
                        </button>
                        <button
                          onClick={() => handleMarkResolved(complaint)}
                          className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-all flex items-center gap-1"
                          data-testid={`resolve-btn-${complaint.id}`}
                        >
                          <CheckCircle size={14} />
                          Resolved
                        </button>
                      </>
                    )}
                    
                    {/* Mark as Resolved for in_progress complaints */}
                    {complaint.status === 'in_progress' && (
                      <button
                        onClick={() => handleMarkResolved(complaint)}
                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-all flex items-center gap-1"
                        data-testid={`resolve-btn-${complaint.id}`}
                      >
                        <CheckCircle size={14} />
                        Mark as Resolved
                      </button>
                    )}
                    
                    <button
                      onClick={() => navigate(`/officer/cases/${complaint.case_id}`)}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1"
                      data-testid={`view-case-${complaint.case_id}`}
                    >
                      View Case
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resolution Under Process Modal */}
        {showInProgressModal && selectedComplaint && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Resolution Under Process</h2>
                  <p className="text-sm text-slate-500">Complaint: {selectedComplaint.subject}</p>
                </div>
                <button
                  onClick={() => setShowInProgressModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What resolution are you working on? *
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none resize-none"
                    placeholder="Describe the resolution steps you are taking..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Estimated Time of Resolution *
                  </label>
                  <input
                    type="text"
                    value={estimatedTime}
                    onChange={(e) => setEstimatedTime(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
                    placeholder="e.g., 2-3 business days, Within 24 hours, By end of week"
                  />
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowInProgressModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={submitInProgress}
                  disabled={submitting || !resolutionNotes || !estimatedTime}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save & Notify Client'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark as Resolved Modal */}
        {showResolvedModal && selectedComplaint && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Mark as Resolved</h2>
                  <p className="text-sm text-slate-500">Complaint: {selectedComplaint.subject}</p>
                </div>
                <button
                  onClick={() => setShowResolvedModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Resolution Details *
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none resize-none"
                    placeholder="Describe how the complaint was resolved..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Was the client satisfied with the resolution? *
                  </label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setClientSatisfied(true)}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                        clientSatisfied === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <ThumbsUp size={20} />
                      Yes, Satisfied
                    </button>
                    <button
                      onClick={() => setClientSatisfied(false)}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 flex items-center justify-center gap-2 font-medium transition-all ${
                        clientSatisfied === false
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <ThumbsDown size={20} />
                      Not Satisfied
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowResolvedModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={submitResolved}
                  disabled={submitting || !resolutionNotes || clientSatisfied === null}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Mark as Resolved'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
